import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { DeleteHospitalMapper } from '../../../mappers/system/hospital/delete-hospital.mapper';
import { PostHospitalMapper } from '../../../mappers/system/hospital/post-hospital.mapper';
import { PutHospitalMapper } from '../../../mappers/system/hospital/put-hospital.mapper';
import { DataResponseModel } from '../../../models/apis/data-response.model';
import { StatusResponseModel } from '../../../models/apis/status-response.model';
import { HospitalModel } from '../../../models/apis/system/hospital/hospital.model';
import { PostHospitalRequestModel } from '../../../models/apis/system/hospital/post-hospital-request.model';
import { PutHospitalRequestModel } from '../../../models/apis/system/hospital/put-hospital-request.model';
import { HospitalHttpService } from './http/hospital.http.service';
import { GetHospitalsPaginatedMapper } from '../../../mappers/system/hospital/get-hospitals-paginated.mapper';
import { GetHospitalByIdMapper } from '../../../mappers/system/hospital/get-hospital-by-id.mapper';
import { GetHospitalsPaginatedModel } from '../../../models/apis/system/hospital/get-hospitals-paginated.model';
import { HospitalQueryModel } from '../../../models/apis/system/hospital/hospital-query.model';
import { DeleteHospitalPictureCollapsedMapper } from '../../../mappers/system/hospital/delete-hospital-picture-collapsed.mapper';
import { DeleteHospitalPictureExpandedMapper } from '../../../mappers/system/hospital/delete-hospital-picture-expanded.mapper';
import { GetHospitalsLitePaginatedMapper } from '../../../mappers/system/hospital/get-hospitals-lite-paginated.mapper';
import { GetHospitalsLitePaginatedModel } from '../../../models/apis/system/hospital/get-hospitals-lite-paginated.model';
import { HospitalLiteQueryModel } from '../../../models/apis/system/hospital/hospital-lite-query.model';

@Injectable({
    providedIn: 'root',
})
export class HospitalService {
    private readonly _hospitalHttpService = inject(HospitalHttpService);
    private readonly _getHospitalsPaginatedMapper = inject(
        GetHospitalsPaginatedMapper
    );
    private readonly _getHospitalsLitePaginatedMapper = inject(
        GetHospitalsLitePaginatedMapper
    );
    private readonly _getHospitalByIdMapper = inject(GetHospitalByIdMapper);
    private readonly _postHospitalMapper = inject(PostHospitalMapper);
    private readonly _putHospitalMapper = inject(PutHospitalMapper);
    private readonly _deleteHospitalMapper = inject(DeleteHospitalMapper);
    private readonly _deletePictureCollapsedHospitalMapper = inject(
        DeleteHospitalPictureCollapsedMapper
    );
    private readonly _deletePictureExpandedHospitalMapper = inject(
        DeleteHospitalPictureExpandedMapper
    );

    getPaginated(
        request: GetHospitalsPaginatedModel
    ): Observable<DataResponseModel<HospitalQueryModel>> {
        return this._hospitalHttpService
            .getPaginated(this._getHospitalsPaginatedMapper.convertTo(request))
            .pipe(
                map((response) =>
                    this._getHospitalsPaginatedMapper.convertFrom(response)
                )
            );
    }

    getLitePaginated(
        request: GetHospitalsLitePaginatedModel
    ): Observable<DataResponseModel<HospitalLiteQueryModel>> {
        return this._hospitalHttpService
            .getLitePaginated(this._getHospitalsLitePaginatedMapper.convertTo(request))
            .pipe(
                map((response) =>
                    this._getHospitalsLitePaginatedMapper.convertFrom(response)
                )
            );
    }

    getById(id: number): Observable<DataResponseModel<HospitalModel>> {
        return this._hospitalHttpService
            .getById(id)
            .pipe(
                map((response) =>
                    this._getHospitalByIdMapper.convertFrom(response)
                )
            );
    }

    post(
        request: PostHospitalRequestModel
    ): Observable<DataResponseModel<HospitalModel>> {
        return this._hospitalHttpService
            .post(this._postHospitalMapper.convertTo(request))
            .pipe(
                map((response) =>
                    this._postHospitalMapper.convertFrom(response)
                )
            );
    }

    put(
        request: PutHospitalRequestModel
    ): Observable<DataResponseModel<HospitalModel>> {
        return this._hospitalHttpService
            .put(this._putHospitalMapper.convertTo(request))
            .pipe(
                map((response) => this._putHospitalMapper.convertFrom(response))
            );
    }

    delete(id: number): Observable<StatusResponseModel> {
        return this._hospitalHttpService
            .delete(id)
            .pipe(
                map((response) =>
                    this._deleteHospitalMapper.convertFrom(response)
                )
            );
    }

    deletePictureCollapsed(id: number): Observable<StatusResponseModel> {
        return this._hospitalHttpService
            .deletePictureCollapsed(id)
            .pipe(
                map((response) =>
                    this._deletePictureCollapsedHospitalMapper.convertFrom(
                        response
                    )
                )
            );
    }

    deletePictureExpanded(id: number): Observable<StatusResponseModel> {
        return this._hospitalHttpService
            .deletePictureExpanded(id)
            .pipe(
                map((response) =>
                    this._deletePictureExpandedHospitalMapper.convertFrom(
                        response
                    )
                )
            );
    }
}
